import { useEffect } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, ArrowUpDown, ExternalLink } from "lucide-react";
import { useListings } from "@/api/listings";
import { FitBadge } from "@/components/FitBadge";
import { NewBadge } from "@/components/NewBadge";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { cn, formatDate } from "@/lib/utils";

const STATUS_OPTIONS = [
  { value: "all", label: "All listings" },
  { value: "matched", label: "Matched" },
  { value: "rejected", label: "Rejected" },
  { value: "new", label: "New" },
  { value: "error", label: "Errored" },
];

const BASE_COLUMNS: { key: string; label: string }[] = [
  { key: "fit", label: "Fit" },
  { key: "title", label: "Title" },
  { key: "company", label: "Company" },
  { key: "source", label: "Source" },
  { key: "posted_at", label: "Posted" },
  { key: "deadline", label: "Deadline" },
  { key: "fetched_at", label: "Fetched" },
];

export function Listings() {
  useEffect(() => {
    document.title = "Listings — Beacon";
    return () => { document.title = "Beacon"; };
  }, []);

  const search = useSearch({ from: "/" }) as {
    status?: string;
    source?: string;
    sort?: string;
    dir?: "asc" | "desc";
  };
  const navigate = useNavigate({ from: "/" });

  const status = search.status ?? "matched";
  const source = search.source ?? "";
  const sort = search.sort ?? "fetched_at";
  const dir = search.dir ?? "desc";

  const { data, isLoading, isError, error } = useListings({ status, source, sort, dir });

  const hasDeadline = data?.items.some((r) => r.application_deadline != null) ?? false;
  const columns = hasDeadline ? BASE_COLUMNS : BASE_COLUMNS.filter((c) => c.key !== "deadline");

  const goto = (patch: Partial<typeof search>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }) as never });

  const onSortClick = (col: string) => {
    if (sort === col) {
      goto({ dir: dir === "asc" ? "desc" : "asc" });
    } else {
      goto({ sort: col, dir: "desc" });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">Listings</h1>
        <p className="text-sm text-muted-foreground">
          {STATUS_OPTIONS.find((o) => o.value === status)?.label ?? "All listings"}
          {source ? ` from ${source}` : ""}.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={status} onValueChange={(v) => goto({ status: v })}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-50 bg-white dark:bg-zinc-950">
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={source || "__all__"} onValueChange={(v) => goto({ source: v === "__all__" ? undefined : v })}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="all sources" />
          </SelectTrigger>
          <SelectContent className="z-50 bg-white dark:bg-zinc-950">
            <SelectItem value="__all__">All sources</SelectItem>
            {(data?.sources ?? []).map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isError && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Failed to load listings: {(error as Error).message}
        </div>
      )}

      {/* Mobile card list */}
      <div className="block md:hidden divide-y divide-border rounded-lg border border-border overflow-hidden">
        {isLoading ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="px-4 py-3">
              <div className="h-4 w-3/4 animate-pulse rounded bg-muted/60" />
              <div className="mt-2 h-3 w-1/2 animate-pulse rounded bg-muted/60" />
            </div>
          ))
        ) : data?.items.length ? (
          data.items.map((row) => (
            <Link
              key={row.id}
              to="/listings/$listingId"
              params={{ listingId: String(row.id) }}
              className="flex flex-col gap-1.5 px-4 py-3 hover:bg-muted/30"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <FitBadge value={row.fit_score} />
                <span className="font-medium text-foreground">{row.title}</span>
                {row.is_new && <NewBadge />}
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                <span>{row.company_name ?? "—"}</span>
                <Badge variant="muted">{row.source}</Badge>
                {row.posted_at && <span className="font-mono">{formatDate(row.posted_at)}</span>}
                {row.application_deadline && <span className="font-mono text-amber-500">⏰ {formatDate(row.application_deadline)}</span>}
              </div>
            </Link>
          ))
        ) : (
          <EmptyState
            className="border-0 bg-transparent"
            title="No listings yet"
            description="Add a criterion and run beacon run to start seeing matches here."
            action={
              <Button asChild variant="outline">
                <Link to="/criteria">
                  Manage criteria <ExternalLink size={14} className="ml-1.5" />
                </Link>
              </Button>
            }
          />
        )}
      </div>

      <div className="hidden md:block overflow-hidden rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              {columns.map((col) => {
                const active = sort === col.key;
                return (
                  <th key={col.key} className="px-4 py-2.5 font-medium">
                    <button
                      type="button"
                      onClick={() => onSortClick(col.key)}
                      className={cn(
                        "inline-flex items-center gap-1 transition-colors",
                        active ? "text-foreground" : "hover:text-foreground",
                      )}
                    >
                      {col.label}
                      {active ? (
                        dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                      ) : (
                        <ArrowUpDown size={12} className="opacity-30" />
                      )}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              [...Array(6)].map((_, i) => (
                <tr key={i} className="border-t border-border">
                  <td colSpan={columns.length} className="px-4 py-3">
                    <div className="h-4 w-full animate-pulse rounded bg-muted/60" />
                  </td>
                </tr>
              ))
            ) : data?.items.length ? (
              data.items.map((row) => (
                <tr key={row.id} className="border-t border-border hover:bg-muted/30">
                  <td className="px-4 py-3"><FitBadge value={row.fit_score} /></td>
                  <td className="px-4 py-3">
                    <Link
                      to="/listings/$listingId"
                      params={{ listingId: String(row.id) }}
                      className="font-medium text-foreground hover:text-primary"
                    >
                      {row.title}
                    </Link>
                    {row.is_new && <NewBadge />}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {row.company_name ?? "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="muted">{row.source}</Badge>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    {formatDate(row.posted_at)}
                  </td>
                  {hasDeadline && (
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {formatDate(row.application_deadline) ?? <span className="opacity-30">—</span>}
                    </td>
                  )}
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    {formatDate(row.fetched_at)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length}>
                  <EmptyState
                    className="border-0 bg-transparent"
                    title="No listings yet"
                    description="Add a criterion and run beacon run to start seeing matches here."
                    action={
                      <Button asChild variant="outline">
                        <Link to="/criteria">
                          Manage criteria <ExternalLink size={14} className="ml-1.5" />
                        </Link>
                      </Button>
                    }
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
